import { components } from '@feedfm/clips-services-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStudioAuthenticatedContext } from '../../../context/StudioContext';

export interface FallbackSuggestion {
  audioFileId: number;
  trackTitle: string;
  artistName: string;
  releaseTitle: string;
  matchType: 'track_and_artist' | 'artist_only';
}

export const useSearchWithFallback = () => {
  const { backendClient } = useStudioAuthenticatedContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchResults, setSearchResults] = useState<
    null | components['schemas']['SearchResponse']
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [fallbackSuggestion, setFallbackSuggestion] =
    useState<FallbackSuggestion | null>(null);
  const [fallbackClips, setFallbackClips] = useState<
    components['schemas']['Clip'][]
  >([]);
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const [semanticResults, setSemanticResults] = useState<
    components['schemas']['Clip'][]
  >([]);
  const [semanticSearchTerms, setSemanticSearchTerms] = useState<string[]>([]);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSetUrlQueryRef = useRef<string>('');
  const lastSearchedQueryRef = useRef<string>('');

  const performSearch = useCallback(
    async (query: string) => {
      // Prevent duplicate searches for the same query
      if (lastSearchedQueryRef.current === query) {
        return;
      }
      lastSearchedQueryRef.current = query;

      if (!query.trim()) {
        setSearchResults(null);
        setHasSearched(false);
        setSearchParams({});
        setSemanticResults([]);
        setSemanticSearchTerms([]);
        lastSearchedQueryRef.current = '';
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setHasSearched(true);

        // Update URL with search query without causing performSearch to change identity
        if (lastSetUrlQueryRef.current !== query) {
          setSearchParams({ q: query });
          lastSetUrlQueryRef.current = query;
        }

        // Use the new search API endpoint with separate limits
        const response = await backendClient.search({
          q: query,
          type: 'all',
          collectionsLimit: 3,
          clipsLimit: 5,
        });

        setSearchResults(response);

        // If no clips, start fallback search; only try semantic search if fallback finds nothing
        if ((response?.clips?.length || 0) === 0) {
          setFallbackSuggestion(null);
          setFallbackClips([]);
          setSemanticResults([]);
          setSemanticSearchTerms([]);
          setSemanticLoading(true);

          // Start fallback search in audio catalog; only try semantic search if fallback finds nothing
          // Capture the query for this search to avoid stale results if user types a new query
          const capturedQuery = query;
          (async () => {
            let fallbackFound = false;
            try {
              const fallbackResponse = await backendClient.fallbackSearch({
                q: capturedQuery,
              });
              if (lastSearchedQueryRef.current !== capturedQuery) return;
              const suggestion = fallbackResponse.suggestion as FallbackSuggestion | undefined;
              if (suggestion) {
                fallbackFound = true;
                setFallbackSuggestion(suggestion);
                setFallbackLoading(true);
                try {
                  let response;
                  if (suggestion.matchType === 'track_and_artist') {
                    response = await backendClient.similarClipsByAudioFile({
                      audioFileId: suggestion.audioFileId,
                      limit: 10,
                    });
                  } else {
                    response = await backendClient.similarClipsByArtist({
                      artistName: suggestion.artistName,
                      limit: 10,
                    });
                  }

                  if (lastSearchedQueryRef.current !== capturedQuery) return;

                  const clips: components['schemas']['Clip'][] = (
                    response.similarTracks || []
                  ).map((track) => ({
                    clipId: track.clipId ?? track.trackEmbeddingId,
                    trackId: track.trackEmbeddingId,
                    title: track.title,
                    artist: track.displayArtistName,
                    album: track.album ?? '',
                    duration: track.duration ?? 0,
                    preSignedUrl: track.preSignedUrl ?? '',
                    artworkPreSignedUrl: track.artworkPreSignedUrl ?? undefined,
                    preSignedUrlExpires: new Date(
                      Date.now() + 24 * 60 * 60 * 1000,
                    ).toISOString(),
                    encoding: '',
                    bitrate: 0,
                    explicitness: 'unknown' as const,
                    createDate: new Date().toISOString(),
                  }));

                  setFallbackClips(clips);
                } catch (_e) {
                  // Silently fail
                } finally {
                  setFallbackLoading(false);
                }
              }
            } catch (_e) {
              // Silently ignore fallback errors
            }

            if (lastSearchedQueryRef.current !== searchQuery) return;

            // Only try semantic search if fallback found nothing
            if (!fallbackFound) {
              try {
                const semanticResponse = await backendClient.semanticSearch({
                  query: capturedQuery,
                  limit: 5,
                });

                if (lastSearchedQueryRef.current !== capturedQuery) return;

                const seen = new Set<string>();
                const clips: components['schemas']['Clip'][] = (
                  semanticResponse.results || []
                ).reduce<components['schemas']['Clip'][]>((acc, result) => {
                  const key = `${result.title.trim().toLowerCase()}|${result.artist.trim().toLowerCase()}`;
                  if (seen.has(key)) return acc;
                  seen.add(key);
                  acc.push({
                    clipId: result.clipId ?? result.trackEmbeddingId,
                    trackId: result.trackEmbeddingId,
                    title: result.title,
                    artist: result.artist,
                    album: result.album ?? '',
                    duration: result.duration ?? 0,
                    preSignedUrl: result.preSignedUrl ?? '',
                    artworkPreSignedUrl: result.artworkPreSignedUrl ?? undefined,
                    preSignedUrlExpires: new Date(
                      Date.now() + 24 * 60 * 60 * 1000,
                    ).toISOString(),
                    encoding: '',
                    bitrate: 0,
                    explicitness: 'unknown' as const,
                    createDate: new Date().toISOString(),
                  });
                  return acc;
                }, []);

                setSemanticResults(clips);
                setSemanticSearchTerms(semanticResponse.searchTerms ?? []);
              } catch (_e) {
                setSemanticResults([]);
              } finally {
                setSemanticLoading(false);
              }
            } else {
              setSemanticLoading(false);
            }
          })();
        } else {
          setFallbackSuggestion(null);
          setFallbackClips([]);
          setSemanticResults([]);
          setSemanticSearchTerms([]);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'An error occurred while searching',
        );
      } finally {
        setLoading(false);
      }
    },
    [backendClient, setSearchParams],
  );

  // Handle initial load from URL parameters (only on mount)
  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== searchQuery) {
      setSearchQuery(urlQuery);
    }
    // We rely on the debounced effect below to perform the search.
    // This avoids duplicate calls (URL-load + debounce).
  }, []);

  // Debounced search effect for user input
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, performSearch]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    loading,
    error,
    hasSearched,
    fallbackSuggestion,
    fallbackClips,
    fallbackLoading,
    semanticResults,
    semanticSearchTerms,
    semanticLoading,
  };
};
